import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import {
  EmailSchema,
  ValidUuidOrNotSetSchema,
  withoutObjectKeysWhereValueIs,
} from '@vestigas/utils';
import { log } from '~/utils';

import { queryKeysInvoiceAiModel } from './queryKeys';

const zodUpdateInvoiceAiModelSenderEndpointSchema = z.tuple([
  z.literal('vadmin'),
  z.literal('invoice'),
  z.literal('ai_model_settings'),
  z.uuid(),
  z.literal('sender'),
  z.uuid(),
]);

const zodUpdateInvoiceAiModelSenderPayloadSchema = z
  .object({
    id: ValidUuidOrNotSetSchema,
    companyDomain: z
      .union([z.string().min(3).includes('.'), z.literal('')])
      .nullish(),
    senderAddress: z.union([EmailSchema, z.literal('')]).nullish(),
  })
  .refine(
    ({ companyDomain, senderAddress }) => {
      return [senderAddress, companyDomain].some(Boolean);
    },
    { error: 'Sender address or company domain must be provided' },
  )
  .transform((data) => withoutObjectKeysWhereValueIs(data, [null, undefined]));

type UpdateInvoiceAiModelSenderPayload = z.infer<
  typeof zodUpdateInvoiceAiModelSenderPayloadSchema
>;

type UpdateInvoiceAiModelSenderResponse = {
  code: number;
  message: string;
  requestId: UUID | undefined;
};

/**
 * Updates an invoice AI model sender.
 *
 * @param invoiceAiModelSettingsId - The ID of the invoice AI model settings.
 * @param senderId - The ID of the sender to update.
 * @param senderData - The data to update the sender with.
 * @returns - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/update_invoice_ai_model_sender_vadmin_invoice_ai_model_settings__model_id__sender__sender_id__put
 */
export const updateInvoiceAiModelSender = async ({
  invoiceAiModelSettingsId,
  senderId,
  senderData,
}: {
  invoiceAiModelSettingsId: UUID;
  senderId: UUID;
  senderData: UpdateInvoiceAiModelSenderPayload;
}) => {
  try {
    const response = await vestigasApi
      .put(
        ENDPOINT.INVOICE_AI_MODEL.SETTINGS.UPDATE_SENDER(
          invoiceAiModelSettingsId,
          senderId,
        ),
        {
          json: senderData,
          validate: {
            pathSegments: zodUpdateInvoiceAiModelSenderEndpointSchema,
          },
        },
      )
      .json<UpdateInvoiceAiModelSenderResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error updating invoice AI model sender',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error; // re-throw error so it can be handled higher up in the callstack.
  }
};

/**
 * Custom hook for updating invoice AI model sender using mutations.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/update_invoice_ai_model_sender_vadmin_invoice_ai_model_settings__model_id__sender__sender_id__put
 */
export const useMutationUpdateInvoiceAiModelSender = (
  options?: UseMutationOptions<
    UpdateInvoiceAiModelSenderResponse,
    unknown,
    {
      invoiceAiModelSettingsId: UUID;
      senderId: UUID;
      senderData: UpdateInvoiceAiModelSenderPayload;
    }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      invoiceAiModelSettingsId,
      senderId,
      senderData,
    }: {
      invoiceAiModelSettingsId: UUID;
      senderId: UUID;
      senderData: UpdateInvoiceAiModelSenderPayload;
    }) =>
      updateInvoiceAiModelSender({
        invoiceAiModelSettingsId,
        senderId,
        senderData,
      }),
    onSettled(_data, _error, { invoiceAiModelSettingsId }) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceAiModel.getSettings(invoiceAiModelSettingsId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceAiModel.getAllSettings({}),
      });
    },
    ...options,
  });
};
